// =============================================================================
// GESTION DES TEMPLATES DE DOCUMENTS GÉNÉRABLES (Moteur Documents)
// =============================================================================
// Liste des templates figés avec versioning pour génération CEE/MPR
// =============================================================================

import { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Search,
  Edit,
  Eye,
  Trash2,
  Lock,
  Unlock,
  RefreshCw,
  Filter,
  Calendar,
  Tag,
  Building2,
  CheckCircle2,
  AlertCircle,
  FileCode,
  Hash,
  Save
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Types de documents
const TYPES_DOCUMENT = {
  cadre_contribution: { label: "Cadre de Contribution", color: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" },
  attestation_honneur: { label: "Attestation sur l'Honneur", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" },
  mandat_administratif: { label: "Mandat Administratif", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" },
  fiche_standardisee: { label: "Fiche Standardisée", color: "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]" },
  synthese_aides: { label: "Synthèse des Aides", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" },
  autre: { label: "Autre", color: "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" }
};

// Types d'aide (demandeur)
const TYPES_AIDE = {
  cee: { label: "CEE", color: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]", icon: "🌱" },
  mpr: { label: "MPR", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]", icon: "🏠" },
  tous: { label: "CEE + MPR", color: "bg-[var(--oh-stat-cyan-bg)] text-[var(--oh-stat-cyan-text)]", icon: "🎯" }
};

// Phases
const PHASES = {
  avant_travaux: { label: "Avant Travaux", color: "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]" },
  apres_travaux: { label: "Après Travaux", color: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" }
};

// Statuts requis
const STATUTS_REQUIS = {
  validation: { label: "En validation", color: "bg-yellow-500" },
  locked: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]" },
  devis_signe: { label: "Devis signé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" },
  chantier_planifie: { label: "Chantier planifié", color: "bg-[var(--oh-accent-solid)]" },
  chantier_termine: { label: "Chantier terminé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
  dossier_depose: { label: "Dossier déposé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
  paye: { label: "Payé", color: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]" }
};

// Délégataires (Obligés/Pollueurs)
const DELEGATAIRES = {
  premium_energy: { label: "Premium Energy", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
};

export default function DocumentsEngineTemplates({ tenantId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterPhase, setFilterPhase] = useState("all");
  const [filterAide, setFilterAide] = useState("all");
  
  // Listes configurables chargées depuis l'API
  const [listesConfig, setListesConfig] = useState({
    types_document: [],
    types_aide: [],
    phases_document: [],
    statuts_requis: [],
    delegataires: []
  });
  
  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialog détail
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Dialog création / édition
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = création, sinon = édition
  const [newTemplate, setNewTemplate] = useState({
    code: "",
    nom: "",
    type_document: "cadre_contribution",
    phase: "avant_travaux",
    aide_type: "cee",
    delegataire: "",
    statut_requis: "validation",
    contenu_html: "<html><body><h1>Nouveau Template</h1></body></html>",
    actif: true
  });
  const [creating, setCreating] = useState(false);

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  useEffect(() => {
    loadTemplates();
    loadListesConfig();
  }, [tenantId]);
  
  // Charger les listes configurables
  const loadListesConfig = async () => {
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/admin/listes`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setListesConfig(data.listes || {});
      }
    } catch (error) {
      console.error("Erreur chargement listes:", error);
    }
  };
  
  // Helpers pour convertir les listes en objets utilisables
  const getTypesDocument = () => {
    const items = listesConfig.types_document || [];
    if (items.length === 0) return TYPES_DOCUMENT;
    return items.reduce((acc, item) => {
      acc[item.code] = { label: item.label, color: item.color ? `${item.color}/20 ${item.color.replace('bg-', 'text-')}` : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" };
      return acc;
    }, {});
  };
  
  const getTypesAide = () => {
    const items = listesConfig.types_aide || [];
    if (items.length === 0) return TYPES_AIDE;
    return items.reduce((acc, item) => {
      acc[item.code] = { label: item.label, color: item.color ? `${item.color}/20 ${item.color.replace('bg-', 'text-')}` : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]", icon: item.icon || "" };
      return acc;
    }, {});
  };
  
  const getPhases = () => {
    const items = listesConfig.phases_document || [];
    if (items.length === 0) return PHASES;
    return items.reduce((acc, item) => {
      acc[item.code] = { label: item.label, color: item.color ? `${item.color}/20 ${item.color.replace('bg-', 'text-')}` : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" };
      return acc;
    }, {});
  };
  
  const getStatutsRequis = () => {
    const items = listesConfig.statuts_requis || [];
    if (items.length === 0) return STATUTS_REQUIS;
    return items.reduce((acc, item) => {
      acc[item.code] = { label: item.label, color: item.color || "bg-zinc-500/20 border border-[var(--oh-border)]" };
      return acc;
    }, {});
  };
  
  const getDelegataires = () => {
    const items = listesConfig.delegataires || [];
    if (items.length === 0) return DELEGATAIRES;
    return items.reduce((acc, item) => {
      acc[item.code] = { label: item.label, color: item.color || "bg-zinc-500/20 border border-[var(--oh-border)]" };
      return acc;
    }, {});
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/templates`, { 
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
        setSelectedIds(new Set());
        setSelectAll(false);
      } else {
        toast.error("Erreur lors du chargement des templates");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  // Créer un nouveau template
  const handleCreateTemplate = async () => {
    if (!newTemplate.code || !newTemplate.nom) {
      toast.error("Code et nom obligatoires");
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/templates`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(newTemplate)
      });
      
      if (res.ok) {
        toast.success("Template créé avec succès");
        setShowCreateDialog(false);
        setNewTemplate({
          code: "",
          nom: "",
          type_document: "cadre_contribution",
          phase: "avant_travaux",
          aide_type: "cee",
          statut_requis: "locked",
          contenu_html: "<html><body><h1>Nouveau Template</h1></body></html>",
          actif: true
        });
        loadTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la création");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de connexion");
    } finally {
      setCreating(false);
    }
  };

  // Ouvrir dialog pour modification
  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      code: template.code || "",
      nom: template.nom || "",
      type_document: template.type_document || "cadre_contribution",
      phase: template.phase || "avant_travaux",
      aide_type: template.aide_type || "cee",
      delegataire: template.delegataire || "",
      statut_requis: template.statut_requis || "validation",
      contenu_html: template.contenu_html || "<html><body></body></html>",
      actif: template.actif !== false
    });
    setShowCreateDialog(true);
  };

  // Ouvrir dialog pour création
  const openCreateDialog = () => {
    setEditingTemplate(null);
    setNewTemplate({
      code: "",
      nom: "",
      type_document: "cadre_contribution",
      phase: "avant_travaux",
      aide_type: "cee",
      delegataire: "",
      statut_requis: "validation",
      contenu_html: "<html><body><h1>Nouveau Template</h1></body></html>",
      actif: true
    });
    setShowCreateDialog(true);
  };

  // Mettre à jour un template existant
  const handleUpdate = async () => {
    if (!editingTemplate) return;
    
    if (!newTemplate.nom) {
      toast.error("Le nom est obligatoire");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/templates/id/${editingTemplate.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(newTemplate)
      });

      if (res.ok) {
        toast.success("Template mis à jour");
        setShowCreateDialog(false);
        setEditingTemplate(null);
        loadTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de connexion");
    } finally {
      setCreating(false);
    }
  };

  // Filtrage
  const filteredTemplates = templates.filter(t => {
    const matchSearch = !searchTerm || 
      t.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || t.type_document === filterType;
    const matchPhase = filterPhase === "all" || t.phase === filterPhase;
    const matchAide = filterAide === "all" || t.aide_type === filterAide;
    return matchSearch && matchType && matchPhase && matchAide;
  });

  // Gestion sélection
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === filteredTemplates.length && filteredTemplates.length > 0);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(filteredTemplates.map(t => t.id)));
      setSelectAll(true);
    }
  };

  // Supprimer sélection
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("Aucun template sélectionné");
      return;
    }
    if (!window.confirm(`Supprimer ${selectedIds.size} template(s) ?`)) return;

    let deleted = 0;
    for (const id of selectedIds) {
      try {
        const res = await fetch(`${API}/api/metier/documents-engine/templates/id/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders()
        });
        if (res.ok) deleted++;
      } catch {}
    }

    if (deleted > 0) {
      toast.success(`${deleted} template(s) supprimé(s)`);
      loadTemplates();
    }
  };

  // Supprimer un template individuel
  const handleDeleteSingle = async (template) => {
    if (!window.confirm(`Supprimer le template "${template.nom}" ?`)) return;
    
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/templates/id/${template.id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success("Template supprimé");
        loadTemplates();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Toggle actif/inactif
  const toggleTemplateActif = async (template) => {
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/templates/id/${template.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...template, actif: !template.actif })
      });
      if (res.ok) {
        toast.success(template.actif ? "Template désactivé" : "Template activé");
        loadTemplates();
      }
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Ouvrir détail
  const openDetail = (template) => {
    setSelectedTemplate(template);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-engine-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[var(--oh-info-text)]" />
            Templates Documents Générables
          </h2>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            Templates figés pour la génération automatique des documents CEE/MPR
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Actions groupées */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)]">
              <span className="text-sm text-[var(--oh-text-muted)]">
                {selectedIds.size} sélectionné(s)
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-templates"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedIds(new Set()); setSelectAll(false); }}
              >
                Annuler
              </Button>
            </div>
          )}
          <Button onClick={loadTemplates} variant="outline" className="border-[var(--oh-border)]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={openCreateDialog} 
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Template
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)]">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
          <Input
            placeholder="Rechercher par nom ou code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full max-w-[180px] bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
            <SelectValue placeholder="Type de document" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {Object.entries(getTypesDocument()).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPhase} onValueChange={setFilterPhase}>
          <SelectTrigger className="w-full max-w-[160px] bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
            <SelectValue placeholder="Phase" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes phases</SelectItem>
            {Object.entries(getPhases()).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAide} onValueChange={setFilterAide}>
          <SelectTrigger className="w-full max-w-[140px] bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
            <SelectValue placeholder="Aide" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes aides</SelectItem>
            {Object.entries(getTypesAide()).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tableau */}
      <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
              <TableHead className="w-10">
                <Checkbox
                  checked={selectAll}
                  onCheckedChange={toggleSelectAll}
                  className="border-[var(--oh-border)]"
                  data-testid="select-all-templates"
                />
              </TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Nom du Template</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Version</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Date création</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Demandeur</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Délégataire</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Cible</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Phase</TableHead>
              <TableHead className="text-[var(--oh-text-muted)]">Statut requis</TableHead>
              <TableHead className="text-[var(--oh-text-muted)] text-center">Actif</TableHead>
              <TableHead className="text-[var(--oh-text-muted)] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-12">
                  <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-3" />
                  <p className="text-[var(--oh-text-muted)]">Aucun template trouvé</p>
                  <p className="text-[var(--oh-text-secondary)] text-sm">
                    {searchTerm || filterType !== "all" || filterPhase !== "all"
                      ? "Essayez d'autres filtres"
                      : "Les templates seront ajoutés par l'administrateur"}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => {
                const isSelected = selectedIds.has(template.id);
                const typesDoc = getTypesDocument();
                const typesAide = getTypesAide();
                const phases = getPhases();
                const statuts = getStatutsRequis();
                const delegataires = getDelegataires();
                const typeInfo = typesDoc[template.type_document] || typesDoc.autre || { label: "Autre", color: "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" };
                const aideInfo = typesAide[template.aide_type] || typesAide.cee || { label: "CEE", color: "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" };
                const phaseInfo = phases[template.phase] || {};
                const statutInfo = statuts[template.statut_requis] || {};
                const delegataireInfo = delegataires[template.delegataire] || null;
                
                return (
                  <TableRow 
                    key={template.id} 
                    className={`border-[var(--oh-border)] ${isSelected ? 'bg-[var(--oh-accent-bg)]' : ''} ${!template.actif ? 'opacity-50' : ''}`}
                  >
                    <TableCell className="w-10">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(template.id)}
                        className="border-[var(--oh-border)]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeInfo.color.split(' ')[0]}`}>
                          <FileText className={`w-4 h-4 ${typeInfo.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <p className="text-[var(--oh-text)] font-medium">{template.nom}</p>
                          <p className="text-[var(--oh-text-secondary)] text-xs font-mono">{template.code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] font-mono">
                        v{template.version || "1"}.00
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[var(--oh-text-muted)] text-sm">
                      {formatDate(template.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge className={aideInfo.color}>
                        {aideInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {delegataireInfo ? (
                        <Badge className={`${delegataireInfo.color} text-[var(--oh-text)]`}>
                          {delegataireInfo.label}
                        </Badge>
                      ) : (
                        <span className="text-[var(--oh-text-secondary)] text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={typeInfo.color}>
                        {typeInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={phaseInfo.color || "bg-[var(--oh-bg-elevated)]"}>
                        {phaseInfo.label || template.phase}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statutInfo.color || 'bg-slate-600'} text-[var(--oh-accent-contrast)] text-xs`}>
                        {statutInfo.label || template.statut_requis}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {template.actif ? (
                        <CheckCircle2 className="w-5 h-5 text-[var(--oh-success-text)] mx-auto" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-[var(--oh-text-secondary)] mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(template)}
                          title="Voir le détail"
                        >
                          <Eye className="w-4 h-4 text-[var(--oh-text-muted)]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(template)}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4 text-[var(--oh-accent-text)]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleTemplateActif(template)}
                          title={template.actif ? "Désactiver" : "Activer"}
                        >
                          {template.actif ? (
                            <Lock className="w-4 h-4 text-[var(--oh-info-text)]" />
                          ) : (
                            <Unlock className="w-4 h-4 text-[var(--oh-success-text)]" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSingle(template)}
                          title="Supprimer"
                          className="hover:bg-[var(--oh-error-bg)]"
                        >
                          <Trash2 className="w-4 h-4 text-[var(--oh-error-text)]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-[var(--oh-text-secondary)]">
        <span>{filteredTemplates.length} template(s) affiché(s)</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            CEE: {templates.filter(t => t.aide_type === 'cee').length}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[var(--oh-accent-solid)]"></span>
            MPR: {templates.filter(t => t.aide_type === 'mpr').length}
          </span>
        </div>
      </div>

      {/* Dialog Détail Template */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FileCode className="w-5 h-5 text-[var(--oh-info-text)]" />
              Détail du Template
            </DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-6 py-4">
              {/* Info principale */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Nom</label>
                  <p className="text-[var(--oh-text)] font-medium">{selectedTemplate.nom}</p>
                </div>
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Code</label>
                  <p className="text-[var(--oh-info-text)] font-mono">{selectedTemplate.code}</p>
                </div>
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Version</label>
                  <p className="text-[var(--oh-text)]">v{selectedTemplate.version || "1"}.00</p>
                </div>
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Date de création</label>
                  <p className="text-[var(--oh-text)]">{formatDate(selectedTemplate.created_at)}</p>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={getTypesAide()[selectedTemplate.aide_type]?.color || "bg-[var(--oh-bg-elevated)]"}>
                  {getTypesAide()[selectedTemplate.aide_type]?.label || selectedTemplate.aide_type}
                </Badge>
                <Badge className={getTypesDocument()[selectedTemplate.type_document]?.color || "bg-[var(--oh-bg-elevated)]"}>
                  {getTypesDocument()[selectedTemplate.type_document]?.label || selectedTemplate.type_document}
                </Badge>
                <Badge className={getPhases()[selectedTemplate.phase]?.color || "bg-[var(--oh-bg-elevated)]"}>
                  {getPhases()[selectedTemplate.phase]?.label || selectedTemplate.phase}
                </Badge>
                <Badge className="bg-[var(--oh-bg-elevated)]">
                  Statut requis: {getStatutsRequis()[selectedTemplate.statut_requis]?.label || selectedTemplate.statut_requis}
                </Badge>
              </div>

              {/* Description */}
              {selectedTemplate.description && (
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Description</label>
                  <p className="text-[var(--oh-text-muted)] text-sm">{selectedTemplate.description}</p>
                </div>
              )}

              {/* Variables requises */}
              {selectedTemplate.variables_requises?.length > 0 && (
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Variables requises</label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedTemplate.variables_requises.map((v, i) => (
                      <code key={i} className="text-xs bg-[var(--oh-bg-elevated)] text-[var(--oh-success-text)] px-2 py-0.5 rounded">
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* Variables optionnelles */}
              {selectedTemplate.variables_optionnelles?.length > 0 && (
                <div>
                  <label className="text-xs text-[var(--oh-text-secondary)]">Variables optionnelles</label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedTemplate.variables_optionnelles.map((v, i) => (
                      <code key={i} className="text-xs bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] px-2 py-0.5 rounded">
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* Métadonnées */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-[var(--oh-bg-elevated)] rounded-lg">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                  <div>
                    <label className="text-xs text-[var(--oh-text-secondary)]">ID</label>
                    <p className="text-[var(--oh-text-muted)] font-mono text-xs">{selectedTemplate.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                  <div>
                    <label className="text-xs text-[var(--oh-text-secondary)]">Dernière mise à jour</label>
                    <p className="text-[var(--oh-text-muted)] text-xs">{formatDate(selectedTemplate.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de création / modification */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) setEditingTemplate(null);
      }}>
        <DialogContent className="max-w-2xl bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--oh-text)]">
              {editingTemplate ? (
                <>
                  <Edit className="w-5 h-5 text-[var(--oh-accent-text)]" />
                  Modifier le Template
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-[var(--oh-success-text)]" />
                  Nouveau Template
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code et Nom */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Code *</label>
                <Input
                  placeholder="Ex: ATT_CEE_2025"
                  value={newTemplate.code}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  disabled={!!editingTemplate}
                />
                {editingTemplate && (
                  <p className="text-xs text-[var(--oh-text-secondary)] mt-1">Le code ne peut pas être modifié</p>
                )}
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Nom *</label>
                <Input
                  placeholder="Ex: Attestation CEE 2025"
                  value={newTemplate.nom}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, nom: e.target.value }))}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                />
              </div>
            </div>

            {/* Type, Phase, Aide, Délégataire */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Type de document</label>
                <Select 
                  value={newTemplate.type_document} 
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, type_document: v }))}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(getTypesDocument()).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Phase</label>
                <Select 
                  value={newTemplate.phase} 
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, phase: v }))}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(getPhases()).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Type d'aide</label>
                <Select 
                  value={newTemplate.aide_type} 
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, aide_type: v }))}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(getTypesAide()).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.icon} {val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Délégataire (Obligé)</label>
                <Select 
                  value={newTemplate.delegataire || "none"} 
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, delegataire: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tous / Non spécifié</SelectItem>
                    {Object.entries(getDelegataires()).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Statut requis */}
            <div>
              <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Statut requis du dossier</label>
              <Select 
                value={newTemplate.statut_requis} 
                onValueChange={(v) => setNewTemplate(prev => ({ ...prev, statut_requis: v }))}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(getStatutsRequis()).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contenu HTML */}
            <div>
              <label className="text-sm text-[var(--oh-text-muted)] mb-1 block">Contenu HTML</label>
              <textarea
                value={newTemplate.contenu_html}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, contenu_html: e.target.value }))}
                className="w-full h-32 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-md p-3 text-sm font-mono text-[var(--oh-text-muted)]"
                placeholder="<html><body>...</body></html>"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setEditingTemplate(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={editingTemplate ? handleUpdate : handleCreateTemplate} 
              className={editingTemplate ? "bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]" : "bg-emerald-600 hover:bg-emerald-700"}
              disabled={creating || !newTemplate.nom || (!editingTemplate && !newTemplate.code)}
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {editingTemplate ? "Mise à jour..." : "Création..."}
                </>
              ) : editingTemplate ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Mettre à jour
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
